<template>
  <div class="password-protected-form">
    <h2>Enter Password</h2>
    <form name="access-form" class="access-form" @submit.prevent="handleAccess">
      <div class="access-form__group">
        <label class="access-form__group-label" for="password">Password</label>
        <div class="access-form__password-wrapper">
          <input
            class="access-form__group-input"
            :type="showPassword ? 'text' : 'password'"
            id="password"
            autocomplete="current-password"
            placeholder="Password *"
            v-model="password"
            required
          />
          <button
              type="button"
              class="access-form__toggle-password"
              @click="togglePassword"
              :aria-label="showPassword ? 'Hide password' : 'Show password'"
          >
            <IconEye v-if="!showPassword" class="access-form__toggle-icon" />
            <IconEyeClosed v-else class="access-form__toggle-icon" />
          </button>
        </div>
      </div>
      <button class="access-form__button" type="submit">
        {{ submitButtonText }}
      </button>
    </form>
  
    <div v-if="message" :class="['access-form__message', messageType]" @click="message = ''">
      {{ message }}
    </div>
  </div>
</template>

<script lang="ts">
import { getRecaptchaToken, loadRecaptcha, unmountRecaptcha } from "~/composables/grecaptcha";
import { passwordProtectedAccessMutation } from "~/hooks/mutations/password-protected-access-mutation-handler";
import { defineAsyncComponent } from "#imports";

const IconEye = defineAsyncComponent(() => import('~/assets/icons/IconEye.svg'));
const IconEyeClosed = defineAsyncComponent(() => import('~/assets/icons/IconEyeClosed.svg'));

export default {
  emits: [
    'access-successful'
  ],
  props: {
    postId: Number,
    submitButtonText: {
      type: String,
      default: 'View Page'
    }
  },
  components: {
    IconEye,
    IconEyeClosed,
  },
  data () {
    return {
      password: '',
      message: '',
      messageType: '',
      showPassword: false,
    };
  },
  mounted() {
    loadRecaptcha();

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  },
  unmounted() {
    unmountRecaptcha()
  },
  methods: {
    async handleAccess() {
      try {
        this.resetMessage()

        const token = await getRecaptchaToken();

        if (!token) {
          throw new Error('reCAPTCHA validation failed');
        }

        const { data } = await passwordProtectedAccessMutation({ postId: this.postId, password: this.password, captchaToken: token });

        if (data?.passwordProtectedAccess?.status === 'SUCCESS') {
          this.$emit('access-successful');
          return
        } else {
          this.message = 'There was an error. Please try again or contact support.';
          this.messageType = 'error';
        }
      } catch (error) {
        this.message = error.message || 'There was an error. Please try again or contact support.';
        this.messageType = 'error';
      }
    },
    resetMessage() {
      this.message = ''
      this.messageType = ''
    },
    togglePassword() {
      this.showPassword = ! this.showPassword;
    }
  },
};
</script>

<style lang="scss">
@use "~/common/scss/form";

.password-protected-form {
  text-align: center;
  margin-top: 2rem;
}

.access-form {
  @include form.baseForm('__');
  max-width: 400px;
  margin: 2rem auto;

  &__password-wrapper {
    position: relative;
  }

  &__toggle-password {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    cursor: pointer;
    color: #666;
    padding: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      color: #333;
    }

    &:focus {
      outline: none;
    }
  }

  &__toggle-icon {
    width: 75%;
    height: 75%;
    pointer-events: none;
  }

  &__group-input[type="password"],
  &__group-input[type="text"] {
    padding-right: 2.5rem;
  }
}
</style>
